// Multi-track audio extraction. The browser's decodeAudioData collapses a file
// to a single (mixed/first) audio track, so to put each audio track of an MP4/MOV
// on its own timeline lane we demux with mp4box and decode each track with
// WebCodecs AudioDecoder. Anything that isn't a multi-track MP4 (single-track
// MP4, WebM/MKV, plain audio, or any failure) falls back to decodeAudioData.
import { createFile, MP4BoxBuffer, type Track, type Sample } from "mp4box";
import { demuxMatroskaAudio } from "./demux-matroska";

// AAC sampling-frequency index table (ISO/IEC 14496-3).
const FREQ_INDEX: Record<number, number> = {
  96000: 0, 88200: 1, 64000: 2, 48000: 3, 44100: 4, 32000: 5,
  24000: 6, 22050: 7, 16000: 8, 12000: 9, 11025: 10, 8000: 11, 7350: 12,
};

/** Build a 2-byte AudioSpecificConfig for AAC from codec string + format.
 *  WebCodecs' AAC decoder needs this `description` for raw (non-ADTS) samples. */
function aacAsc(codec: string, sampleRate: number, channels: number): Uint8Array | undefined {
  const m = /mp4a\.40\.(\d+)/.exec(codec);
  if (!m) return undefined; // not AAC — let the decoder try without a description
  const aot = Number(m[1]) || 2;
  const fi = FREQ_INDEX[sampleRate];
  if (fi === undefined) return undefined;
  const b0 = (aot << 3) | (fi >> 1);
  const b1 = ((fi & 1) << 7) | ((channels & 0x0f) << 3);
  return new Uint8Array([b0, b1]);
}

function isMp4Family(file: File): boolean {
  const n = file.name.toLowerCase();
  return file.type.includes("mp4") || file.type === "video/quicktime" || /\.(mp4|m4v|mov|m4a)$/.test(n);
}
function isMatroska(file: File): boolean {
  const n = file.name.toLowerCase();
  return file.type.includes("webm") || file.type.includes("matroska") || /\.(mkv|webm)$/.test(n);
}

export interface AudioExtractResult { buffers: AudioBuffer[]; reason: string }

const isMemoryError = (e: any): boolean =>
  e instanceof RangeError || /memory|allocat|too large|invalid array length|maximum call|buffer allocation/i.test(String(e?.message ?? e));

/** One AudioBuffer per audio track in the file, plus a human reason when none could
 *  be extracted (unsupported codec, out of memory, …). MP4/MOV use mp4box, MKV/WebM
 *  the streaming EBML demuxer; anything else falls back to a single decodeAudioData. */
export async function extractAudioBuffers(file: File, ctx: BaseAudioContext): Promise<AudioExtractResult> {
  const mp4 = isMp4Family(file), mkv = isMatroska(file);
  const diag: { codecs: string[] } = { codecs: [] };
  let reason = "";
  if (typeof AudioDecoder !== "undefined" && (mp4 || mkv)) {
    try {
      const tracks = mp4 ? await demuxMp4Audio(file, ctx, diag) : await demuxMatroskaAudio(file, ctx, diag);
      // Use the demuxed tracks whenever we got at least one (avoids a second
      // whole-file read, and decodeAudioData can't read a Matroska container).
      if (tracks.length >= 1) return { buffers: tracks, reason: "" };
      // Parsed OK but couldn't decode any track → almost always an unsupported codec.
      if (diag.codecs.length) {
        reason = `audio codec ${[...new Set(diag.codecs)].join(", ")} isn't supported by this browser`;
        if (mkv) return { buffers: [], reason }; // no decodeAudioData fallback for MKV
      }
    } catch (e: any) {
      reason = isMemoryError(e) ? "the file is too large to read into memory" : (e?.message || "audio demux failed");
      console.warn("[webcut] audio demux failed:", e);
      if (mkv) return { buffers: [], reason };
    }
  }
  // Non-MP4/MKV containers, or MP4 where the demuxer found nothing: one mixed decode.
  try { return { buffers: [await ctx.decodeAudioData(await file.arrayBuffer())], reason: "" }; }
  catch (e: any) {
    if (!reason) reason = isMemoryError(e) ? "the file is too large to read into memory for audio" : "no browser-decodable audio track";
    return { buffers: [], reason };
  }
}

/** Demux + WebCodecs-decode every audio track in an MP4/MOV file → one buffer
 *  per track. Exported for testing; callers normally go through extractAudioBuffers. */
export async function demuxMp4Audio(file: File, ctx: BaseAudioContext, diag?: { codecs: string[] }): Promise<AudioBuffer[]> {
  const mp4 = createFile();
  const ab = await file.arrayBuffer();

  // Set extraction up in onReady and start() *before* flush() delivers samples —
  // that's the order mp4box requires, otherwise onSamples never fires.
  const { tracks, samples } = await new Promise<{ tracks: Track[]; samples: Map<number, Sample[]> }>((resolve, reject) => {
    let audioTracks: Track[] = [];
    const acc = new Map<number, Sample[]>();
    const timer = setTimeout(() => resolve({ tracks: audioTracks, samples: acc }), 15000); // never hang
    const allCollected = () => audioTracks.length > 0 && audioTracks.every((t) => (acc.get(t.id)?.length ?? 0) >= (t.nb_samples || 0));

    mp4.onError = (_m: string, msg: string) => { clearTimeout(timer); reject(new Error("mp4box: " + msg)); };
    mp4.onReady = (info) => {
      audioTracks = (info.tracks || []).filter((t) => !!t.audio);
      if (diag) diag.codecs = audioTracks.map((t) => t.codec);
      if (!audioTracks.length) { clearTimeout(timer); resolve({ tracks: [], samples: acc }); return; }
      for (const t of audioTracks) { acc.set(t.id, []); mp4.setExtractionOptions(t.id, null, { nbSamples: t.nb_samples }); }
      mp4.start();
    };
    mp4.onSamples = (id: number, _user: unknown, s: Sample[]) => {
      const a = acc.get(id);
      if (a) for (const x of s) a.push(x);
      if (allCollected()) { clearTimeout(timer); resolve({ tracks: audioTracks, samples: acc }); }
    };
    mp4.appendBuffer(MP4BoxBuffer.fromArrayBuffer(ab, 0));
    mp4.flush();
  });

  const out: AudioBuffer[] = [];
  for (const t of tracks) {
    const buf = await decodeAudioSamples(t, samples.get(t.id) ?? [], ctx);
    if (buf) out.push(buf);
  }
  return out;
}

/** Decode one audio track's encoded samples to an AudioBuffer via WebCodecs. */
async function decodeAudioSamples(track: Track, samples: Sample[], ctx: BaseAudioContext): Promise<AudioBuffer | null> {
  if (!samples.length || !track.audio) return null;
  const sampleRate = track.audio.sample_rate;
  const channels = track.audio.channel_count;
  const desc = aacAsc(track.codec, sampleRate, channels);

  const chunks: { data: Float32Array[]; frames: number }[] = [];
  let outCh = channels, outRate = sampleRate, failed = false;

  const dec = new AudioDecoder({
    output: (ad) => {
      outCh = ad.numberOfChannels; outRate = ad.sampleRate;
      const frames = ad.numberOfFrames;
      const data: Float32Array[] = [];
      for (let c = 0; c < outCh; c++) {
        const arr = new Float32Array(frames);
        ad.copyTo(arr, { planeIndex: c, format: "f32-planar" });
        data.push(arr);
      }
      chunks.push({ data, frames });
      ad.close();
    },
    error: (e) => { failed = true; console.warn("[webcut] AudioDecoder error", e); },
  });

  try {
    dec.configure({ codec: track.codec, sampleRate, numberOfChannels: channels, ...(desc ? { description: desc } : {}) });
    for (const s of samples) {
      if (!s.data) continue;
      dec.decode(new EncodedAudioChunk({
        type: s.is_sync ? "key" : "delta",
        timestamp: Math.round((s.cts / s.timescale) * 1e6),
        duration: Math.round((s.duration / s.timescale) * 1e6),
        data: s.data,
      }));
    }
    await dec.flush();
  } catch (e) {
    console.warn("[webcut] audio track decode failed", e);
    try { dec.close(); } catch {}
    return null;
  }
  try { dec.close(); } catch {}

  if (failed) return null;
  return assembleAudioBuffer(ctx, chunks, outCh, outRate);
}

/** Assemble decoded PCM chunks into one AudioBuffer. A single AudioBuffer can't
 *  exceed a few hundred MB (a ~2h stereo track needs ~3GB), so for very long audio
 *  we down-mix to mono and box-average downsample to fit — speech stays intelligible.
 *  Shared by the MP4 and MKV demuxers. */
export function assembleAudioBuffer(
  ctx: BaseAudioContext,
  chunks: { data: Float32Array[]; frames: number }[],
  channels: number,
  rate: number,
): AudioBuffer | null {
  const total = chunks.reduce((n, c) => n + c.frames, 0);
  if (!total) return null;
  const CAP = 500 * 1024 * 1024; // max bytes for one (contiguous) AudioBuffer
  let outCh = channels, k = 1;
  const fits = () => Math.floor(total / k) * outCh * 4 <= CAP;
  if (!fits() && outCh > 1) outCh = 1;                 // stereo → mono first
  while (!fits() && rate / (k + 1) >= 3000) k++;       // then decimate (keep rate ≥ 3000 Hz)

  try {
    // Fast path: fits as-is → straight copy, preserving channels + rate.
    if (outCh === channels && k === 1) {
      const buf = ctx.createBuffer(channels, total, rate);
      for (let c = 0; c < channels; c++) {
        const dst = buf.getChannelData(c); let off = 0;
        for (const ch of chunks) { dst.set(ch.data[Math.min(c, ch.data.length - 1)], off); off += ch.frames; }
      }
      return buf;
    }
    // Long audio: mono, box-averaged decimation by k → buffer at rate/k.
    const newLen = Math.floor(total / k);
    const buf = ctx.createBuffer(1, Math.max(1, newLen), rate / k);
    const dst = buf.getChannelData(0);
    let outIdx = 0, acc = 0, cnt = 0;
    for (const ch of chunks) {
      const L = ch.data[0], R = channels > 1 && ch.data[1] ? ch.data[1] : null;
      for (let f = 0; f < ch.frames; f++) {
        acc += R ? (L[f] + R[f]) * 0.5 : L[f];
        if (++cnt === k) { if (outIdx < newLen) dst[outIdx++] = acc / k; acc = 0; cnt = 0; }
      }
    }
    console.info(`[webcut] long audio (${(total / rate / 60).toFixed(0)} min) down-mixed to ${Math.round(rate / k)}Hz mono to fit the AudioBuffer limit.`);
    return buf;
  } catch (e) {
    console.warn("[webcut] audio buffer assembly failed:", e);
    return null;
  }
}
